/**
 * ============================================
 * ConnectionBadge.tsx — Индикатор подключения
 * ============================================
 * Показывает статус соединения с сервером:
 * - Зелёная точка: online (SSE активно)
 * - Жёлтая пульсирующая: reconnecting (переподключение)
 * - Красная точка: offline (нет связи)
 */

import React from 'react';
import type { ConnectionState } from './useSchedule';

// ============================================
// Props
// ============================================

interface ConnectionBadgeProps {
  /** Текущее состояние подключения */
  state: ConnectionState;
  /** Дополнительные CSS классы */
  className?: string;
}

// ============================================
// Компонент
// ============================================

/**
 * Индикатор подключения к серверу.
 * Отображает цветную точку и текстовый статус.
 * Оптимизирован для чтения с расстояния на TV-экране.
 */
export const ConnectionBadge: React.FC<ConnectionBadgeProps> = React.memo(
  function ConnectionBadge({ state, className = '' }) {
    // Определяем класс точки и текст в зависимости от состояния
    const dotClass =
      state === 'online'
        ? 'tv-connection__dot--online'
        : state === 'reconnecting'
          ? 'tv-connection__dot--reconnecting'
          : 'tv-connection__dot--offline';

    const label =
      state === 'online'
        ? 'Онлайн'
        : state === 'reconnecting'
          ? 'Подключение...'
          : 'Нет связи';

    return (
      <div className={`tv-connection ${className}`} aria-label={`Статус подключения: ${label}`}>
        {/* Цветная точка-индикатор */}
        <span className={`tv-connection__dot ${dotClass}`} aria-hidden="true" />

        {/* Текстовый статус */}
        <span className="tv-connection__label">{label}</span>
      </div>
    );
  }
);

// Экспорт типов для удобства импорта
export type { ConnectionBadgeProps };
